import { useCallback, useRef } from 'react';

export function on<T extends Window | Document | HTMLElement | EventTarget>(
	obj: T | null,
	...args:
		| Parameters<T['addEventListener']>
		| [string, Function | null, ...any]
): void {
	if (obj && obj.addEventListener) {
		obj.addEventListener(
			...(args as Parameters<HTMLElement['addEventListener']>),
		);
	}
}

export function off<T extends Window | Document | HTMLElement | EventTarget>(
	obj: T | null,
	...args:
		| Parameters<T['removeEventListener']>
		| [string, Function | null, ...any]
): void {
	if (obj && obj.removeEventListener) {
		obj.removeEventListener(
			...(args as Parameters<HTMLElement['removeEventListener']>),
		);
	}
}

interface Options {
	isPreventDefault?: boolean;
	delay?: number;
}

const isTouchEvent = (ev: Event): ev is TouchEvent => {
	return 'touches' in ev;
};

const preventDefault = (ev: Event) => {
	if (!isTouchEvent(ev)) return;

	if (ev.touches.length < 2 && ev.preventDefault) {
		ev.preventDefault();
	}
};

const useLongPress = (
	callback: (e: TouchEvent | MouseEvent) => void,
	onClick: (vent: TouchEvent | MouseEvent) => void,
	{ isPreventDefault = true, delay = 300 }: Options = {},
) => {
	const timeout = useRef<ReturnType<typeof setTimeout>>();
	const target = useRef<EventTarget>();
	const fired = useRef<boolean>(false);
	const moved = useRef<boolean>(false);

	const start = useCallback(
		(event: TouchEvent | MouseEvent) => {
			// prevent ghost click on mobile devices
			if (isPreventDefault && event.target) {
				on(event.target, 'touchend', preventDefault, {
					passive: false,
				});
				target.current = event.target;
				fired.current = false;
				moved.current = false;
			}
			timeout.current = setTimeout(() => {
				callback(event);
				fired.current = true;
			}, delay);
		},
		[callback, delay, isPreventDefault],
	);

	const clear = useCallback(
		(event: TouchEvent | MouseEvent, triggerOnClick: boolean) => {
			// clearTimeout and removeEventListener
			timeout.current && clearTimeout(timeout.current);

			if (isPreventDefault && target.current) {
				off(target.current, 'touchend', preventDefault);
			}

			// If the long press didn't fire, call the onClick
			if (
				triggerOnClick &&
				fired.current === false &&
				moved.current !== true
			) {
				onClick(event);
			}
		},
		[isPreventDefault, onClick],
	);

	return {
		onMouseDown: (e: any) => start(e),
		onTouchStart: (e: any) => start(e),
		onMouseUp: (e: any) => clear(e, true),
		onMouseLeave: (e: any) => clear(e, false),
		onTouchEnd: (e: any) => clear(e, true),
		onTouchMove: (e: any) => {
			moved.current = true;
			clear(e, false);
		},
	} as const;
};

export default useLongPress;
